/**
 * Cookie Consent Banner Component
 *
 * GDPR-compliant cookie consent with granular control.
 * Stores preferences in localStorage and optionally syncs to server.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CookiePreferences {
  necessary: boolean;  // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface CookieConsentProps {
  storeId: string;
  storeSlug: string;
  primaryColor?: string;
  onConsentChange?: (preferences: CookiePreferences) => void;
}

const CONSENT_STORAGE_KEY = 'cookie_consent';
const CONSENT_VERSION = '1.0';

function getStoredConsent(storeId: string): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(`${CONSENT_STORAGE_KEY}_${storeId}`);
    if (stored) {
      const data = JSON.parse(stored);
      if (data.version === CONSENT_VERSION) {
        return data.preferences;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function storeConsent(storeId: string, preferences: CookiePreferences) {
  localStorage.setItem(`${CONSENT_STORAGE_KEY}_${storeId}`, JSON.stringify({
    version: CONSENT_VERSION,
    preferences,
    timestamp: new Date().toISOString(),
  }));
}

export default function CookieConsent({
  storeId,
  storeSlug,
  primaryColor = '#0d9488',
  onConsentChange,
}: CookieConsentProps) {
  const { t } = useTranslation('store');
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    const stored = getStoredConsent(storeId);
    if (stored) {
      setPreferences(stored);
      onConsentChange?.(stored);
    } else {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [storeId, onConsentChange]);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setPreferences(allAccepted);
    storeConsent(storeId, allAccepted);
    onConsentChange?.(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setPreferences(onlyNecessary);
    storeConsent(storeId, onlyNecessary);
    onConsentChange?.(onlyNecessary);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    storeConsent(storeId, preferences);
    onConsentChange?.(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
          {!showSettings ? (
            // Simple Banner
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t('cookie.title', 'We value your privacy')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('cookie.description', 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.')}
                    {' '}
                    <a
                      href={`/s/${storeSlug}/cookies`}
                      className="text-teal-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('cookie.learnMore', 'Learn more')}
                    </a>
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {t('cookie.acceptAll', 'Accept All')}
                    </button>
                    <button
                      onClick={handleRejectAll}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {t('cookie.rejectAll', 'Reject All')}
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {t('cookie.customize', 'Customize')}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setShowBanner(false)}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            // Detailed Settings
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 text-lg">
                  {t('cookie.managePreferences', 'Manage Cookie Preferences')}
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                {t('cookie.settingsDescription', 'Choose which cookies you want to accept. Your preferences will be saved for this store.')}
              </p>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">
                        {t('cookie.necessary', 'Necessary Cookies')}
                      </h4>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                        {t('cookie.required', 'Required')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('cookie.necessaryDescription', 'These cookies are essential for the website to function properly. They enable basic features like page navigation and access to secure areas.')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-teal-500 rounded-full relative cursor-not-allowed opacity-50">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {t('cookie.analytics', 'Analytics Cookies')}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('cookie.analyticsDescription', 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleToggle('analytics')}
                      className={`w-12 h-6 rounded-full relative transition-colors ${
                        preferences.analytics ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          preferences.analytics ? 'right-1' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {t('cookie.marketing', 'Marketing Cookies')}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('cookie.marketingDescription', 'These cookies are used to track visitors across websites to display relevant advertisements based on their interests.')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleToggle('marketing')}
                      className={`w-12 h-6 rounded-full relative transition-colors ${
                        preferences.marketing ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          preferences.marketing ? 'right-1' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Preferences Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {t('cookie.preferences', 'Preference Cookies')}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('cookie.preferencesDescription', 'These cookies allow the website to remember choices you make and provide enhanced, personalized features.')}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleToggle('preferences')}
                      className={`w-12 h-6 rounded-full relative transition-colors ${
                        preferences.preferences ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          preferences.preferences ? 'right-1' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: primaryColor }}
                >
                  {t('cookie.savePreferences', 'Save Preferences')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('cookie.acceptAll', 'Accept All')}
                </button>
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {t('cookie.rejectAll', 'Reject All')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Export helper to check consent status
export function getCookieConsent(storeId: string): CookiePreferences | null {
  return getStoredConsent(storeId);
}

// Export helper to reset consent (for testing or user request)
export function resetCookieConsent(storeId: string) {
  localStorage.removeItem(`${CONSENT_STORAGE_KEY}_${storeId}`);
}
